/**
 * The product photo gallery.
 *
 * Every photo the suppliers published, hotlinked from their own sites — the
 * same places the prices came from — so each slide is labelled as theirs and
 * links back to the page it was read from.
 *
 * Three rules govern it:
 *
 * **A broken image vanishes, it never apologises.** CDNs refuse hotlinks,
 * pages go away. Any photo that fails to load is dropped from the set, and a
 * gallery with nothing left renders nothing — a broken-image icon says less
 * than nothing.
 *
 * **The slideshow yields to the reader.** It advances on its own only until
 * the reader touches it — one scroll, click or key ends the automation for
 * good — and it never advances at all for anyone who asked their OS for
 * reduced motion, or while the tab is hidden.
 *
 * **Full size means full size.** Clicking any slide opens a lightbox that
 * gives the photo the whole viewport; arrows, swipe and the scroll wheel move
 * through the set, Escape leaves.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  ArrowUpRightIcon, ChevronLeftIcon, ChevronRightIcon, CloseIcon,
} from '@/components/ui/icons'
import { cx, Panel, SectionHeader } from '@/components/ui/primitives'
import type { ProductImage } from '@/types/api'

/** How long each slide holds before the show advances on its own. */
const ADVANCE_MS = 5000

interface ProductGalleryProps {
  images: ProductImage[]
  alt: string
  className?: string
}

export function ProductGallery({ images, alt, className }: ProductGalleryProps) {
  const [broken, setBroken] = useState<string[]>([])
  const slides = useMemo(
    () => images.filter((image) => !broken.includes(image.url)),
    [images, broken],
  )

  const trackRef = useRef<HTMLDivElement>(null)
  const [index, setIndex] = useState(0)
  const indexRef = useRef(0)
  const [lightbox, setLightbox] = useState<number | null>(null)
  const hoveredRef = useRef(false)
  const interactedRef = useRef(false)

  useEffect(() => {
    indexRef.current = index
  }, [index])

  // A photo failing to load can strand the indices past the end of the set.
  useEffect(() => {
    if (index > slides.length - 1) setIndex(Math.max(0, slides.length - 1))
    if (lightbox !== null && lightbox > slides.length - 1) {
      setLightbox(slides.length > 0 ? slides.length - 1 : null)
    }
  }, [slides.length, index, lightbox])

  const markInteracted = useCallback(() => {
    interactedRef.current = true
  }, [])

  const goTo = useCallback(
    (next: number) => {
      const track = trackRef.current
      if (!track || slides.length === 0) return
      const target = ((next % slides.length) + slides.length) % slides.length
      track.scrollTo({ left: target * track.clientWidth, behavior: 'smooth' })
      setIndex(target)
    },
    [slides.length],
  )

  // The slideshow. It stops deferring and starts obeying at the first touch.
  useEffect(() => {
    if (slides.length < 2 || lightbox !== null) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const timer = window.setInterval(() => {
      if (document.hidden || hoveredRef.current || interactedRef.current) return
      goTo(indexRef.current + 1)
    }, ADVANCE_MS)
    return () => window.clearInterval(timer)
  }, [slides.length, lightbox, goTo])

  const onScroll = () => {
    const track = trackRef.current
    if (!track) return
    const next = Math.min(
      Math.round(track.scrollLeft / Math.max(1, track.clientWidth)),
      slides.length - 1,
    )
    setIndex((current) => (next === current ? current : next))
  }

  if (slides.length === 0) return null

  return (
    <Panel className={className}>
      <SectionHeader
        title="Photos"
        description="Hotlinked from the suppliers' listings — the same pages the prices came from. Click a photo to view it full size."
      />

      <div
        role="region"
        aria-roledescription="carousel"
        aria-label={`${alt} — photos`}
        className="relative mt-4"
        onMouseEnter={() => { hoveredRef.current = true }}
        onMouseLeave={() => { hoveredRef.current = false }}
        onPointerDown={markInteracted}
        onWheel={markInteracted}
        onKeyDown={markInteracted}
      >
        {/* The photo plate stays white in both themes — product shots are
            white-filled, and a dark plate makes every one look like a cutout. */}
        <div
          ref={trackRef}
          onScroll={onScroll}
          className="flex snap-x snap-mandatory overflow-x-auto rounded-lg border border-[var(--border-subtle)] bg-white [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {slides.map((image, position) => (
            <button
              key={image.url}
              type="button"
              onClick={() => setLightbox(position)}
              className="h-64 w-full shrink-0 cursor-zoom-in snap-center p-3 sm:h-80"
              aria-label={`View photo ${position + 1} of ${slides.length} at full size`}
            >
              <img
                src={image.url}
                alt={`${alt} — photo ${position + 1}`}
                loading={position === 0 ? 'eager' : 'lazy'}
                referrerPolicy="no-referrer"
                draggable={false}
                onError={() => setBroken((existing) => [...existing, image.url])}
                className="mx-auto h-full max-w-full object-contain"
              />
            </button>
          ))}
        </div>

        {slides.length > 1 ? (
          <>
            <GalleryArrow
              direction="previous"
              onClick={() => { markInteracted(); goTo(index - 1) }}
            />
            <GalleryArrow
              direction="next"
              onClick={() => { markInteracted(); goTo(index + 1) }}
            />
            <span className="pointer-events-none absolute bottom-2 right-2 rounded-full bg-black/55 px-2 py-0.5 text-2xs tabular text-white">
              {index + 1} / {slides.length}
            </span>
          </>
        ) : null}
      </div>

      {slides.length > 1 ? (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
          {slides.map((image, position) => (
            <button
              key={image.url}
              type="button"
              onClick={() => { markInteracted(); goTo(position) }}
              aria-label={`Go to photo ${position + 1}`}
              aria-current={position === index}
              className={cx(
                'h-14 w-14 shrink-0 overflow-hidden rounded-md border bg-white p-0.5 transition-opacity',
                position === index
                  ? 'border-[var(--accent)]'
                  : 'border-[var(--border-subtle)] opacity-60 hover:opacity-100',
              )}
            >
              <img
                src={image.url}
                alt=""
                loading="lazy"
                referrerPolicy="no-referrer"
                draggable={false}
                className="h-full w-full object-contain"
              />
            </button>
          ))}
        </div>
      ) : null}

      {lightbox !== null && slides[lightbox] ? (
        <Lightbox
          slides={slides}
          index={lightbox}
          alt={alt}
          onNavigate={(next) =>
            setLightbox(((next % slides.length) + slides.length) % slides.length)
          }
          onClose={() => setLightbox(null)}
          onBroken={(url) => setBroken((existing) => [...existing, url])}
        />
      ) : null}
    </Panel>
  )
}

/* -- Pieces ----------------------------------------------------------------- */

function GalleryArrow({
  direction,
  onClick,
  large,
}: {
  direction: 'previous' | 'next'
  onClick: () => void
  large?: boolean
}) {
  const previous = direction === 'previous'
  const Icon = previous ? ChevronLeftIcon : ChevronRightIcon
  return (
    <button
      type="button"
      onClick={(event) => { event.stopPropagation(); onClick() }}
      aria-label={previous ? 'Previous photo' : 'Next photo'}
      className={cx(
        'absolute top-1/2 z-10 -translate-y-1/2 rounded-full transition-colors',
        previous ? 'left-2' : 'right-2',
        large
          ? 'p-2.5 text-white/80 hover:bg-white/10 hover:text-white'
          : 'border border-[var(--border-subtle)] bg-[var(--surface-panel)]/90 p-1.5 text-[var(--text-secondary)] shadow-sm hover:text-[var(--text-primary)]',
      )}
    >
      <Icon size={large ? 26 : 16} />
    </button>
  )
}

interface LightboxProps {
  slides: ProductImage[]
  index: number
  alt: string
  onNavigate: (next: number) => void
  onClose: () => void
  onBroken: (url: string) => void
}

function Lightbox({ slides, index, alt, onNavigate, onClose, onBroken }: LightboxProps) {
  const image = slides[index]
  const dialogRef = useRef<HTMLDivElement>(null)
  const wheelAt = useRef(0)
  const touchX = useRef<number | null>(null)

  // The page behind the lightbox must not scroll, and focus moves into the
  // dialog so Escape and the arrows work without a preliminary click.
  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    dialogRef.current?.focus()
    return () => { document.body.style.overflow = previous }
  }, [])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
      else if (event.key === 'ArrowRight') onNavigate(index + 1)
      else if (event.key === 'ArrowLeft') onNavigate(index - 1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [index, onNavigate, onClose])

  // The wheel pages through photos; throttled so one trackpad flick is one
  // photo rather than five.
  const onWheel = (event: React.WheelEvent) => {
    const now = Date.now()
    if (now - wheelAt.current < 350) return
    const delta =
      Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX
    if (Math.abs(delta) < 8) return
    wheelAt.current = now
    onNavigate(index + (delta > 0 ? 1 : -1))
  }

  const onPointerDown = (event: React.PointerEvent) => {
    touchX.current = event.clientX
  }
  const onPointerUp = (event: React.PointerEvent) => {
    if (touchX.current === null) return
    const delta = event.clientX - touchX.current
    touchX.current = null
    if (Math.abs(delta) > 48) onNavigate(index + (delta < 0 ? 1 : -1))
  }

  const closeOnBackdrop = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) onClose()
  }

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={`${alt} — photo ${index + 1} of ${slides.length}`}
      tabIndex={-1}
      onClick={closeOnBackdrop}
      onWheel={onWheel}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      className="fixed inset-0 z-[90] flex flex-col bg-black/90 outline-none backdrop-blur-sm"
    >
      <div className="flex items-center justify-between gap-3 px-4 py-3 text-sm text-white/85">
        <span className="tabular">
          {index + 1} / {slides.length}
        </span>
        <div className="flex items-center gap-1.5">
          {image.source_url ? (
            <a
              href={image.source_url}
              target="_blank"
              rel="noopener noreferrer nofollow"
              onClick={(event) => event.stopPropagation()}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-white/70 transition-colors hover:bg-white/10 hover:text-white"
            >
              Supplier&rsquo;s page
              <ArrowUpRightIcon size={12} />
            </a>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
          >
            <CloseIcon size={18} />
          </button>
        </div>
      </div>

      <div
        className="relative flex min-h-0 flex-1 items-center justify-center px-4 pb-6 sm:px-16"
        onClick={closeOnBackdrop}
      >
        <img
          key={image.url}
          src={image.url}
          alt={`${alt} — photo ${index + 1}`}
          referrerPolicy="no-referrer"
          draggable={false}
          onError={() => onBroken(image.url)}
          className="max-h-full max-w-full select-none object-contain"
        />
        {slides.length > 1 ? (
          <>
            <GalleryArrow large direction="previous" onClick={() => onNavigate(index - 1)} />
            <GalleryArrow large direction="next" onClick={() => onNavigate(index + 1)} />
          </>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}
